import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Button, Spin } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingOutlined,
  InboxOutlined,
  UserOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  IdcardOutlined,
  UserSwitchOutlined,
  TeamOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  FileSearchOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Header, Sider, Content } = AntLayout;

export const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading, isAuthenticated, refreshUser } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
  }, [isAuthenticated, location.pathname, refreshUser]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const hasPermission = (permission: string) => {
    if (user?.roles?.includes('owner')) {
      return true;
    }
    return user?.permissions?.includes(permission) ?? false;
  };

  const canSeePeople = hasPermission('personal.view')
    || hasPermission('business.view')
    || hasPermission('staff.view');

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      visible: hasPermission('reports.view'),
    },
    {
      key: '/people',
      icon: <UserOutlined />,
      label: 'People',
      visible: canSeePeople,
    },
    {
      key: '/calendar',
      icon: <CalendarOutlined />,
      label: 'Calendar',
      visible: hasPermission('calendar.view'),
    },
    {
      key: 'project',
      icon: <FolderOpenOutlined />,
      label: 'Project',
      children: [
        {
          key: '/reports',
          icon: <FileSearchOutlined />,
          label: 'Reports',
          visible: hasPermission('reports.view'),
        },
        {
          key: '/tasks',
          icon: <CheckSquareOutlined />,
          label: 'Tasks',
          visible: hasPermission('tasks.view'),
        },
        {
          key: '/letters',
          icon: <FileTextOutlined />,
          label: 'Letters',
          visible: hasPermission('letters.view'),
        },
        {
          key: '/dispatches',
          icon: <InboxOutlined />,
          label: 'Dispatches',
          visible: hasPermission('dispatches.view'),
        },
      ],
    },
    {
      key: '/files',
      icon: <FolderOutlined />,
      label: 'Files',
      visible: hasPermission('files.view'),
    },
    {
      key: '/quotations',
      icon: <FileTextOutlined />,
      label: 'Quotations',
      visible: hasPermission('quotations.view'),
    },
    {
      key: '/invoices',
      icon: <FileTextOutlined />,
      label: 'Invoices',
      visible: hasPermission('invoices.view'),
    },
    {
      key: '/task-templates',
      icon: <FileTextOutlined />,
      label: 'Task Templates',
      visible: hasPermission('task-templates.view'),
    },
    {
      key: 'access',
      icon: <IdcardOutlined />,
      label: 'Access',
      children: [
        {
          key: '/roles',
          icon: <TeamOutlined />,
          label: 'Roles',
          visible: hasPermission('staff.manage'),
        },
        {
          key: '/permissions',
          icon: <SafetyCertificateOutlined />,
          label: 'Permissions',
          visible: hasPermission('staff.manage'),
        },
      ],
    },
    // {
    //   key: 'settings',
    //   icon: <SettingOutlined />,
    //   label: 'Settings',
    //   children: [
    //     {
    //       key: '/settings/general',
    //       label: 'General',
    //       visible: true,
    //     },
    //     {
    //       key: '/settings/company',
    //       label: 'Company Profile',
    //       visible: true,
    //     },
    //     {
    //       key: '/settings/numbering',
    //       label: 'Numbering Formats',
    //       visible: true,
    //     },
    //     {
    //       key: '/settings/currency',
    //       label: 'Currency',
    //       visible: true,
    //     },
    //     {
    //       key: '/settings/deadlines',
    //       label: 'Deadline Rules',
    //       visible: true,
    //     },
    //   ],
    // },
  ]
    .map((item) => {
      if (!item.children) {
        return item;
      }
      const visibleChildren = item.children.filter((child) => (child as any).visible !== false);
      return { ...item, children: visibleChildren };
    })
    .filter((item) => {
      if ((item as any).visible === false) {
        return false;
      }
      if (item.children && item.children.length === 0) {
        return false;
      }
      return true;
    });

  const userMenuItems = [
    {
      key: 'profile',
      label: 'Profile',
    },
    {
      key: 'settings',
      label: 'Settings',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: logout,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <div style={{ 
          height: 32, 
          margin: 16, 
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {collapsed ? 'BFS' : 'Byte Flow Soution'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={
            location.pathname.startsWith('/reports') || location.pathname.startsWith('/tasks') || location.pathname.startsWith('/letters') || location.pathname.startsWith('/dispatches')
              ? ['project']
              : location.pathname.startsWith('/roles') || location.pathname.startsWith('/permissions')
              ? ['access']
              : location.pathname.startsWith('/settings')
              ? ['settings']
              : []
          }
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <AntLayout style={{ marginLeft: collapsed ? 80 : 200 }}>
        <Header style={{ 
          padding: 0, 
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: 24,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow
          >
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} />
              <span style={{ marginLeft: 8 }}>{user?.name}</span>
            </div>
          </Dropdown>
        </Header>
        <Content
          style={{
            padding: 24,
            minHeight: 280,
            background: '#fff',
            borderRadius: 8,
          }}
        >
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};
